import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOfflineStudents } from "../../../Api/apiUtils";
import ClickToCall from "../../../Telephony/ClickToCall";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  SearchInput,
  Select,
  Toolbar,
  Loader,
  EmptyState,
} from "../../../ui";
import "./offlineStudents.scss";


interface Student {
  studentId: string;
  credentialId: string;
  userId: any;
  studentName: string;
  emailId: string;
  mobileNumber: string;
  profilePhoto?: string;
  countryCode: any;
}
type SearchPayload = {
  name?: string;
  emailId?: string;
  mobileNumber?: string;
  studentId?: string;
  userId?: string;
  userType?: string;
  typeOfStudent?:any;
  offlineCenterId?:any;
};

const StudentPageContainer: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [noResults, setNoResults] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userTypeFilter, setUserTypeFilter] = useState<string>("ALL"); // "ALL", "PAID", or "DEMO"
  const [centerFilter, setCenterFilter] = useState<string>(""); // Center filter: "1", "3", "5", or ""
  
  // Debounce function
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const fetchStudents = async (searchPayload: { 
    name?: string; 
    emailId?: string; 
    mobileNumber?: string; 
    studentId?: string;
    userId?: string;
    offlineCenterId?:any;
    userType?: string;
    typeOfStudent?:any;
  } = {}) => {
    setLoading(true);
    try {
      const fetchedStudents = await getOfflineStudents(0, 500, "offline", searchPayload);
      if (Array.isArray(fetchedStudents)) {
        setStudents(fetchedStudents);
        setNoResults(fetchedStudents.length === 0);
      } else {
        console.error("Fetched data is not an array:", fetchedStudents);
        setNoResults(true);
      }
    } catch (error) {
      console.error("There was an error fetching the student data!", error);
      setNoResults(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCenterFilter = (centerId: string) => {
    setCenterFilter(centerId);
    
    let searchPayload: SearchPayload = {
      typeOfStudent: "offline"
    };
    
    if (centerId) {
      searchPayload.offlineCenterId = centerId;
    }
    
    // Include userType filter if set
    if (userTypeFilter !== "ALL") {
      searchPayload.userType = userTypeFilter;
    }
    
    // Include search query if present
    if (searchQuery.trim() !== "") {
      if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi.test(searchQuery)) {
        searchPayload = { ...searchPayload, emailId: searchQuery };
      } else if (/^\+?\d+$/.test(searchQuery)) {
        const mobileNumber = searchQuery.replace(/^\+91|^\+1|^\+44/, '').replace(/\D/g, '');
        searchPayload = { ...searchPayload, mobileNumber: mobileNumber };
      } else {
        searchPayload = { ...searchPayload, name: searchQuery };
      }
    }
    
    fetchStudents(searchPayload);
  };
  

  const handleFilterChange = (type: string) => {
    setUserTypeFilter(type);
    
    let searchPayload: SearchPayload = {
      typeOfStudent: "offline"
    };
    
    if (type !== "ALL") {
      searchPayload.userType = type;
    }
    
    // Include center filter if set
    if (centerFilter) {
      searchPayload.offlineCenterId = centerFilter;
    }
    
    // Include search query if present
    if (searchQuery.trim() !== "") {
      if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi.test(searchQuery)) {
        searchPayload = { ...searchPayload, emailId: searchQuery };
      } else if (/^\+?\d+$/.test(searchQuery)) {
        // Remove ONLY +91, +1, or +44 prefixes, then keep remaining digits
        const mobileNumber = searchQuery.replace(/^\+91|^\+1|^\+44/, '').replace(/\D/g, '');
        searchPayload = { ...searchPayload, mobileNumber: mobileNumber };
      } else {
        searchPayload = { ...searchPayload, name: searchQuery };
      }
    }
    
    fetchStudents(searchPayload);
  };
  
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      let searchPayload: SearchPayload = {
        typeOfStudent: "offline"
      };
      
      if (query.trim() !== "") {
        if (userTypeFilter === "STUDENTID") {
          searchPayload = { ...searchPayload, studentId: query };
        } else if (userTypeFilter === "STUDENTUSERID") {
          searchPayload = { ...searchPayload, userId: query };
        } else if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi.test(query)) {
          searchPayload = { ...searchPayload, emailId: query };
        } else if (/^\+?\d+$/.test(query)) {
          // Remove ONLY +91, +1, or +44 prefixes, then keep remaining digits
          const mobileNumber = query.replace(/^\+91|^\+1|^\+44/, '').replace(/\D/g, '');
          searchPayload = { ...searchPayload, mobileNumber: mobileNumber };
        } else {
          searchPayload = { ...searchPayload, name: query };
        }
      }
      
      // Include userType filter if not ALL, STUDENTID, or STUDENTUSERID
      if (userTypeFilter !== "ALL" && userTypeFilter !== "STUDENTID" && userTypeFilter !== "STUDENTUSERID") {
        searchPayload = { ...searchPayload, userType: userTypeFilter };
      }
      
      // Include center filter if set
      if (centerFilter) {
        searchPayload.offlineCenterId = centerFilter;
      }
      
      await fetchStudents(searchPayload);
    }, 500),
    [userTypeFilter, centerFilter]
  );


  useEffect(() => {
    // Get centerId from URL params if present
    const centerIdFromUrl = searchParams.get('centerId') || searchParams.get('offlineCenterId');
    const initialCenterId = centerIdFromUrl || "";
    
    if (initialCenterId) {
      setCenterFilter(initialCenterId);
    }
    
    // Build initial search payload
    let searchPayload: SearchPayload = {
      typeOfStudent: "offline"
    };
    
    if (initialCenterId) {
      searchPayload.offlineCenterId = initialCenterId;
    }
    
    // Include userType filter if set
    if (userTypeFilter !== "ALL") {
      searchPayload.userType = userTypeFilter;
    }
    
    fetchStudents(searchPayload);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      debouncedSearch(searchQuery);
    }
  };

  const handleCardClick = (student: Student) => {
    if (student.credentialId && student.studentId) {
      navigate(`/pages/student-profile/${student.credentialId}/${student.studentId}?student=offline`);
    } else {
      console.error("Missing credentialId or studentId");
    }
  };

  function isValidImageUrl(url: string): boolean {
    if (!url) return false;
    try {
      new URL(url);
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
    } catch {
      return false;
    }
  }
  
  function getDisplayContent(student: any): string {
    if (student.studentName && student.studentName.trim().length > 0) {
      return student.studentName.trim().charAt(0).toUpperCase();
    }
    
    if (student.profilePhoto && typeof student.profilePhoto === 'string') {
      return student.profilePhoto.trim().charAt(0).toUpperCase();
    }
    
    return "U";
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Offline"
        title="Student List"
        subtitle="Offline centre students. Filter by type or centre, or search by name, email or mobile."
        actions={
          <Button variant="primary" onClick={() => navigate("/pages/AddOfflineStudent")}>
            Add Student
          </Button>
        }
      />

      <Toolbar>
        <Field label="Student type">
          {(ctl) => (
            <Select
              {...ctl}
              value={userTypeFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              options={[
                { value: "ALL", label: "All Students" },
                { value: "PAID", label: "Paid Students" },
                { value: "DEMO", label: "Demo Students" },
                // { value: "STUDENTID", label: "Student ID" },
                // { value: "STUDENTUSERID", label: "Student User ID" },
              ]}
            />
          )}
        </Field>
        <Field label="Centre">
          {(ctl) => (
            <Select
              {...ctl}
              value={centerFilter}
              onChange={(e) => handleCenterFilter(e.target.value)}
              options={[
                { value: "", label: "All Centers" },
                { value: "1", label: "Chennai" },
                { value: "3", label: "Mumbai" },
                { value: "5", label: "Chennai - OMR" },
                { value: "6", label: "Bangalore" },
              ]}
            />
          )}
        </Field>
        <Field label="Search" className="ds-toolbar__grow">
          {(ctl) => (
            <SearchInput
              {...ctl}
              placeholder={
                userTypeFilter === "STUDENTID" ? "Search by Student ID..." :
                userTypeFilter === "STUDENTUSERID" ? "Search by User ID..." :
                "Search by name, email, or mobile number..."
              }
              value={searchQuery}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
          )}
        </Field>
      </Toolbar>

      {loading ? (
        <Loader label="Loading students..." />
      ) : noResults ? (
        <EmptyState
          title="No students found"
          description="No students found matching your search criteria. Try another centre, student type or search term."
        />
      ) : (
        <Card flush>
          <ul className="offline-students">
            {students.map((student) => {
              const hasPhoto = !!student.profilePhoto && isValidImageUrl(student.profilePhoto);
              return (
                <li
                  className="offline-students__row"
                  key={student.studentId}
                  onClick={() => handleCardClick(student)}
                >
                  <div className="offline-students__avatar">
                    {hasPhoto ? (
                      <img
                        src={student.profilePhoto}
                        alt="user"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={
                        hasPhoto
                          ? "offline-students__initial offline-students__initial--hidden"
                          : "offline-students__initial"
                      }
                    >
                      {getDisplayContent(student)}
                    </div>
                  </div>
                  <div className="offline-students__info">
                    <div className="offline-students__name ds-strong">{student.studentName}</div>
                    <div className="offline-students__email ds-text-muted">{student.emailId || "-"}</div>
                    <div className="offline-students__phone">
                      <ClickToCall
                        number={
                          student.mobileNumber
                            ? `${student.countryCode || ""}${student.mobileNumber}`
                            : undefined
                        }
                        name={student.studentName}
                        sourceRecordId={student.studentId}
                        customerUserId={student.userId ? String(student.userId) : undefined}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </Page>
  );
};

export default StudentPageContainer;
